import { gql, DocumentNode } from '@apollo/client';
import { Skeleton, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import {
	InstallmentStatus,
	InvoiceViewPaymentMethodPaymentInstallmentFragment,
	PaymentProcessor,
	useInvoiceViewPaymentMethod_GetPaymentInstallmentDetailsQuery,
} from '#types';
import { useToast } from '../../../../hooks/useToast';
import styles from './InvoiceViewPaymentMethod.module.scss';

const invoiceViewPaymentMethodPaymentInstallmentFragment = gql`
	fragment invoiceViewPaymentMethodPaymentInstallment on PaymentInstallment {
		id
		status
		paymentType
		processor
	}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const invoiceViewPaymentMethod_getPaymentInstallmentDetails = gql`
	query invoiceViewPaymentMethod_getPaymentInstallmentDetails(
		$where: PaymentInstallmentWhereUniqueInput!
		$data: InvoiceTokenInput
	) {
		getPaymentInstallmentDetails(where: $where, data: $data) {
			type
			last4
		}
	}
`;

const getInitialPaymentMethod = (
	paymentInstallment?: InvoiceViewPaymentMethodPaymentInstallmentFragment
) => {
	if (
		!paymentInstallment ||
		paymentInstallment.status === InstallmentStatus.Unpaid
	) {
		return '-';
	} else if (
		paymentInstallment.paymentType &&
		// so we only show the payment type as a string for offline payments, otherwise it should be the last-4
		paymentInstallment.processor === PaymentProcessor.Offline
	) {
		return paymentInstallment.paymentType;
	} else {
		return <Skeleton className={ styles.fourCharacters } />;
	}
};

export type InvoiceViewPaymentMethodProps = {
	paymentInstallment?: InvoiceViewPaymentMethodPaymentInstallmentFragment;
	guestToken?: string;
};

type InvoiceViewPaymentMethodPropsWithFragment =
	React.FC<InvoiceViewPaymentMethodProps> & {
		fragments: {
			invoiceViewPaymentMethodPaymentInstallmentFragment: DocumentNode;
		};
	};

export const InvoiceViewPaymentMethod: InvoiceViewPaymentMethodPropsWithFragment =
	( { paymentInstallment, guestToken } ) => {
		const { showError } = useToast();
		const paymentMethod = getInitialPaymentMethod( paymentInstallment );

		const { data, error } =
			useInvoiceViewPaymentMethod_GetPaymentInstallmentDetailsQuery( {
				variables: {
					where: { id: paymentInstallment?.id || '' },
					...( guestToken
						? {
							data: { token: guestToken },
						  }
						: {} ),
				},
				skip: typeof paymentMethod === 'string' || !paymentInstallment?.id,
			} );
		useEffect( () => {
			if ( error ) {
				showError( error );
			}
		}, [ error, showError ] );
		const initialPaymentMethod =
			typeof paymentMethod === 'string' ? (
				<Typography data-cy='payment-method'>{ paymentMethod }</Typography>
			) : (
				paymentMethod
			);
		const apiValue = data?.getPaymentInstallmentDetails.last4 ? (
			<Typography data-cy='payment-method'>
				••••{ data.getPaymentInstallmentDetails.last4 }
			</Typography>
		) : undefined;
		return apiValue || initialPaymentMethod;
	};

InvoiceViewPaymentMethod.fragments = {
	invoiceViewPaymentMethodPaymentInstallmentFragment,
};
